import "./RadioButtonControlGroup.scss";

const DEFAULT_TONE = "default";

/**
 * Segmented radio-button-style control group with an animated background pill.
 *
 * Supports two ARIA roles via `role`:
 * - `"radiogroup"` (default): options get `role="radio"` + `aria-checked`.
 * - `"tablist"`: options get `role="tab"` + `aria-selected`, with optional
 *   `controls` (→ `aria-controls`) and `id` for linking to tab panels.
 *
 * Each option may set a `tone` color scheme (`"default"`, `"green"`, `"red"`, `"pending"`).
 * The animated pill picks up the tone of the currently selected option and
 * selected segments tint their text to match (green/red/pending).
 *
 * @param {object} props
 * @param {"tablist" | "radiogroup"} [props.role="radiogroup"] Group-level ARIA role.
 * @param {string} [props.ariaLabel]
 * @param {string} [props.ariaLabelledBy]
 * @param {string | number | null} props.value Currently selected option value.
 * @param {(value: string | number | null) => void} props.onChange
 * @param {Array<{
 *   value: string | number | null,
 *   label: React.ReactNode,
 *   id?: string,
 *   controls?: string,
 *   tone?: "default" | "green" | "red" | "pending",
 *   disabled?: boolean,
 *   tooltip?: string,
 *   title?: string,
 * }>} props.options
 * @param {string} [props.minCh] CSS length for each segment's min-width (e.g. `"11ch"`).
 *   Defaults to the longest of the component's built-in sizing (`11ch`).
 * @param {string} [props.className] Extra class merged onto the root.
 * @param {boolean} [props.disabled=false] Disables all segments when true.
 * @returns {import('react').JSX.Element}
 */
const RadioButtonControlGroup = ({
    role = "radiogroup",
    ariaLabel,
    ariaLabelledBy,
    value,
    onChange,
    options,
    minCh,
    className,
    disabled = false,
}) => {
    const isTablist = role === "tablist";
    const segmentRole = isTablist ? "tab" : "radio";

    const activeIndex = options.findIndex((opt) => opt.value === value);
    const hasSelection = activeIndex >= 0;
    const activeOption = hasSelection ? options[activeIndex] : undefined;
    const pillTone = activeOption?.tone ?? DEFAULT_TONE;

    const rootStyle = {
        "--segment-count": options.length,
        "--active-index": hasSelection ? activeIndex : 0,
    };
    if (minCh) {
        rootStyle["--segment-min-ch"] = minCh;
    }

    const rootClassName = [
        "radio-button-control-group",
        disabled ? "is-disabled" : null,
        className,
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <div
            className={rootClassName}
            style={rootStyle}
            role={role}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            aria-disabled={disabled || undefined}
        >
            {hasSelection ? (
                <span
                    className={`radio-button-control-group__pill radio-button-control-group__pill--${pillTone}`}
                    aria-hidden
                />
            ) : null}
            {options.map((option) => {
                const tone = option.tone ?? DEFAULT_TONE;
                const isSelected = option.value === value;
                const isSegmentDisabled = disabled || option.disabled === true;
                const tooltip = option.tooltip || option.title;
                const segmentClassName = [
                    "radio-button-control-group__segment",
                    `radio-button-control-group__segment--${tone}`,
                    isSelected ? "is-selected" : null,
                    isSegmentDisabled ? "is-disabled" : null,
                ]
                    .filter(Boolean)
                    .join(" ");

                const ariaStateProps = isTablist
                    ? {
                          "aria-selected": isSelected,
                          "aria-controls": option.controls,
                          "aria-disabled": isSegmentDisabled ? "true" : undefined,
                      }
                    : {
                          "aria-checked": isSelected,
                          "aria-disabled": isSegmentDisabled ? "true" : undefined,
                      };

                return (
                    <button
                        key={String(option.value)}
                        type="button"
                        role={segmentRole}
                        id={option.id}
                        className={segmentClassName}
                        disabled={isSegmentDisabled}
                        title={tooltip}
                        onClick={() => {
                            if (isSegmentDisabled) {
                                return;
                            }
                            onChange(option.value);
                        }}
                        {...ariaStateProps}
                    >
                        {option.label}
                    </button>
                );
            })}
        </div>
    );
};

export default RadioButtonControlGroup;
